import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Stack,
  Chip,
  Fade,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Psychology as TalentBotIcon,
  Person as PersonIcon,
  Minimize as MinimizeIcon,
  RestartAlt as ClearIcon,
} from '@mui/icons-material';
import { sendMessageToAria } from '../../services/groqApi';

const SUGGESTIONS = [
  'How do I complete my review?',
  'How does the manager review work?',
  'How do I assign a form to an employee?',
  "What's the difference between Annual and Probation review?",
];

const INITIAL_MESSAGE = {
  id: 1,
  text: "Hi! I'm TalentBot, your Talent Sculptors Career Excellence Platform assistant. I can help you navigate performance reviews, explain features, answer FAQs, or just chat. What can I help you with?",
  sender: 'bot',
  timestamp: new Date(),
};

const TALENTBOT_GRADIENT = 'linear-gradient(135deg, #0a0068 0%, #3d0099 100%)';

// Animation durations
const OPEN_DURATION = 420;
const CLOSE_DURATION = 280;

const ChatBot = () => {
  const theme = useTheme();
  // animState: 'closed' | 'opening' | 'open' | 'closing'
  const [animState, setAnimState] = useState('closed');
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const animTimerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => { if (animTimerRef.current) clearTimeout(animTimerRef.current); };
  }, []);

  const openChat = () => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    setIsMinimized(false);
    setAnimState('opening');
    animTimerRef.current = setTimeout(() => setAnimState('open'), OPEN_DURATION);
  };

  const closeChat = () => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    setAnimState('closing');
    animTimerRef.current = setTimeout(() => setAnimState('closed'), CLOSE_DURATION);
  };

  const isWindowVisible = animState !== 'closed';
  const isOpen = animState === 'open' || animState === 'opening' || animState === 'closing';

  const sendMessage = async (text) => {
    const trimmed = (text || inputValue).trim();
    if (!trimmed || isLoading) return;

    const userMessage = { id: Date.now(), text: trimmed, sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const reply = await sendMessageToAria(trimmed, messages);
      setMessages(prev => [...prev, { id: Date.now() + 1, text: reply, sender: 'bot', timestamp: new Date() }]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{ ...INITIAL_MESSAGE, timestamp: new Date() }]);
  };

  const formatTime = (ts) => ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Scale effect — window shrinks/grows linearly from the bottom-right (icon position)
  const genieAnimStyle = {
    transformOrigin: 'bottom right',
    '@keyframes scaleOpen': {
      '0%':   { transform: 'scale(0)', opacity: 0 },
      '100%': { transform: 'scale(1)', opacity: 1 },
    },
    '@keyframes scaleClose': {
      '0%':   { transform: 'scale(1)', opacity: 1 },
      '100%': { transform: 'scale(0)', opacity: 0 },
    },
    animation:
      animState === 'opening'
        ? `scaleOpen ${OPEN_DURATION}ms linear forwards`
        : animState === 'closing'
        ? `scaleClose ${CLOSE_DURATION}ms linear forwards`
        : 'none',
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>

      {/* Chat window — genie animation anchor at bottom-right (icon position) */}
      {isWindowVisible && !isMinimized && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            ...genieAnimStyle,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: 400,
              height: 560,
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${alpha('#0a0068', 0.12)}`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: TALENTBOT_GRADIENT,
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    background: alpha('#ffffff', 0.2),
                    width: 36,
                    height: 36,
                    border: `2px solid ${alpha('#ffffff', 0.4)}`,
                  }}
                >
                  <TalentBotIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                    TalentBot
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: '#4ade80',
                        boxShadow: '0 0 6px #4ade80',
                      }}
                    />
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>
                      by Talent Sculptors
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Clear chat" placement="top">
                  <IconButton
                    size="small"
                    onClick={clearChat}
                    sx={{ color: 'white', '&:hover': { bgcolor: alpha('#ffffff', 0.12) } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => setIsMinimized(true)}
                  sx={{ color: 'white', '&:hover': { bgcolor: alpha('#ffffff', 0.12) } }}
                >
                  <MinimizeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={closeChat}
                  sx={{ color: 'white', '&:hover': { bgcolor: alpha('#ffffff', 0.12) } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                background: '#f8f9fc',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {messages.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  {msg.sender === 'bot' && (
                    <Avatar sx={{ background: TALENTBOT_GRADIENT, width: 26, height: 26, flexShrink: 0 }}>
                      <TalentBotIcon sx={{ fontSize: 15 }} />
                    </Avatar>
                  )}
                  <Box sx={{ maxWidth: '78%' }}>
                    <Paper
                      elevation={msg.sender === 'user' ? 2 : 1}
                      sx={{
                        p: '10px 14px',
                        background: msg.sender === 'user' ? TALENTBOT_GRADIENT : 'white',
                        color: msg.sender === 'user' ? 'white' : 'text.primary',
                        borderRadius: 2.5,
                        borderTopRightRadius: msg.sender === 'user' ? 4 : 2.5,
                        borderTopLeftRadius: msg.sender === 'bot' ? 4 : 2.5,
                        borderBottomLeftRadius: msg.sender === 'bot' ? 0.5 : 2.5,
                        borderBottomRightRadius: msg.sender === 'user' ? 0.5 : 2.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {msg.text}
                      </Typography>
                    </Paper>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.disabled',
                        display: 'block',
                        textAlign: msg.sender === 'user' ? 'right' : 'left',
                        mt: 0.4,
                        px: 0.5,
                        fontSize: '0.6rem',
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  </Box>
                  {msg.sender === 'user' && (
                    <Avatar sx={{ bgcolor: alpha('#0a0068', 0.15), width: 26, height: 26, flexShrink: 0 }}>
                      <PersonIcon sx={{ fontSize: 15, color: '#0a0068' }} />
                    </Avatar>
                  )}
                </Box>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <Avatar sx={{ background: TALENTBOT_GRADIENT, width: 26, height: 26 }}>
                    <TalentBotIcon sx={{ fontSize: 15 }} />
                  </Avatar>
                  <Paper
                    elevation={1}
                    sx={{ p: '10px 14px', bgcolor: 'white', borderRadius: 2.5, borderTopLeftRadius: 4, borderBottomLeftRadius: 0.5 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {[0, 0.2, 0.4].map((delay, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#0a0068',
                            opacity: 0.6,
                            animation: 'tb-bounce 1.2s infinite',
                            animationDelay: `${delay}s`,
                            '@keyframes tb-bounce': {
                              '0%, 60%, 100%': { transform: 'translateY(0)' },
                              '30%': { transform: 'translateY(-5px)' },
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Quick questions — only on first open */}
            {messages.length === 1 && (
              <Box
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.5,
                  borderTop: `1px solid ${alpha('#0a0068', 0.08)}`,
                  bgcolor: 'white',
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.68rem' }}>
                  Quick questions:
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  {SUGGESTIONS.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      clickable
                      disabled={isLoading}
                      onClick={() => sendMessage(s)}
                      sx={{
                        bgcolor: alpha('#0a0068', 0.07),
                        color: '#0a0068',
                        fontSize: '0.68rem',
                        height: 22,
                        '&:hover': { bgcolor: alpha('#0a0068', 0.14) },
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Input */}
            <Box
              sx={{
                p: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: 'white',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  variant="outlined"
                  placeholder="Ask TalentBot anything..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f8f9fc',
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <IconButton
                  onClick={() => sendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  sx={{
                    background: TALENTBOT_GRADIENT,
                    color: 'white',
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    '&:hover': { background: 'linear-gradient(135deg, #0c0082 0%, #5000bb 100%)' },
                    '&.Mui-disabled': { background: alpha('#0a0068', 0.2), color: 'white' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Minimized pill */}
      {isOpen && isMinimized && (
        <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
          <Paper
            elevation={4}
            onClick={() => setIsMinimized(false)}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 5,
              background: TALENTBOT_GRADIENT,
              color: 'white',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              '&:hover': { transform: 'scale(1.03)', transition: 'transform 0.15s ease' },
            }}
          >
            <TalentBotIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight="bold">TalentBot</Typography>
          </Paper>
        </Box>
      )}

      {/* FAB toggle button */}
      <Fade in={animState === 'closed'} timeout={250}>
        <Tooltip title="Chat with TalentBot — your Career Excellence assistant" placement="left">
          <IconButton
            onClick={openChat}
            sx={{
              width: 58,
              height: 58,
              background: TALENTBOT_GRADIENT,
              color: 'white',
              boxShadow: theme.shadows[8],
              '&:hover': {
                background: 'linear-gradient(135deg, #0c0082 0%, #5000bb 100%)',
                transform: 'scale(1.08)',
                boxShadow: theme.shadows[12],
              },
              transition: 'all 0.25s ease',
            }}
          >
            <TalentBotIcon sx={{ fontSize: 26 }} />
          </IconButton>
        </Tooltip>
      </Fade>
    </Box>
  );
};

export default ChatBot;
